import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { DataProviderInstance } from '../../api/DataProvider';
import { formatGraceDeadline, isPastDueInGrace } from '../../utils/pastDue';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import styles from './PastDueBanner.module.css';

const DISMISS_KEY = 'gabriela.pastDueBannerDismissed';

/**
 * Banner exibido quando a assinatura está com pagamento pendente (past_due)
 * e ainda dentro do período de tolerância.
 *
 * Mostra a data limite e leva direto ao portal do Stripe para atualizar o cartão.
 * Pode ser fechado, mas volta a aparecer no próximo login (sessionStorage).
 */
const PastDueBanner = () => {
  const { subscription } = useSubscription();
  const { CreateStripeCustomerPortal } = DataProviderInstance();
  // Equipe vê o aviso, mas só responsável ou administrador abre o portal de pagamento
  const { canAccess } = useRoleAccess();
  const canManage = canAccess('subscription');

  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (e) {
      return false;
    }
  });
  const [loading, setLoading] = useState(false);

  if (dismissed || !isPastDueInGrace(subscription)) return null;

  const deadline = formatGraceDeadline(subscription?.past_due_grace_ends_at);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      // sessionStorage indisponível — apenas esconde nesta renderização
    }
    setDismissed(true);
  };

  const handleOpenPortal = () => {
    if (loading) return;
    setLoading(true);

    CreateStripeCustomerPortal((result) => {
      if (result && result.success && result.url) {
        window.location.href = result.url;
        return;
      }
      setLoading(false);
    });
  };

  return (
    <div className={styles.banner}>
      <span className={styles.message}>
        {deadline
          ? `Pagamento pendente. Atualize seu cartão até ${deadline} para manter o acesso.`
          : 'Pagamento pendente. Atualize seu cartão para manter o acesso.'}
        {!canManage && ' Só a pessoa responsável pela conta ou um administrador pode atualizar o pagamento.'}
      </span>
      <button
        className={styles.ctaButton}
        onClick={handleOpenPortal}
        disabled={loading || !canManage}
        title={canManage ? undefined : 'Só a pessoa responsável pela conta ou um administrador pode atualizar o pagamento.'}
      >
        {loading ? 'Abrindo...' : 'Atualizar pagamento'}
      </button>
      <button
        className={styles.dismissButton}
        onClick={handleDismiss}
        aria-label="Fechar aviso"
      >
        <CloseIcon fontSize="inherit" />
      </button>
    </div>
  );
};

export default PastDueBanner;
